import React from 'react';
import {IconButton, Typography, Tooltip} from '@material-ui/core';
import {ChevronLeft, ChevronRight} from '@material-ui/icons';
import {StyledTableCell} from './StyledTableCell';

/**
 * Navigation cell component for column carousel
 * @param {string} direction - 'left' or 'right'
 * @param {Function} onClick - Click handler
 * @param {boolean} disabled - Whether navigation is disabled
 * @param {Object} theme - Material-UI theme object
 * @param {boolean} isEmpty - If true, renders empty cell (for body rows)
 * @param {number} remainingCount - Number of remaining metrics in this direction
 * @param {Array<string>} metricKeys - Array of metric keys in this direction
 * @param {Object} metricsConfig - Metrics configuration object
 */
const CarouselNavigationCell = ({
  direction,
  onClick,
  disabled,
  theme,
  isEmpty = false,
  style,
  remainingCount = 0,
  metricKeys = [],
  metricsConfig = null,
}) => {
  const EMPTY_CELL_WIDTH = 40;
  const NAV_CELL_WIDTH = 96;

  if (isEmpty) {
    return (
      <StyledTableCell
        align="center"
        style={{
          width: EMPTY_CELL_WIDTH,
          padding: 8,
          verticalAlign: 'middle',
          backgroundColor: theme.palette.background.paper,
          ...(style || {}),
        }}
      />
    );
  }

  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  const showCount = remainingCount > 0;

  // Build tooltip content with metric display names
  const tooltipContent = React.useMemo(() => {
    if (!metricsConfig || metricKeys.length === 0) return null;

    return (
      <div>
        {metricKeys.map((metricKey, index) => {
          const config = metricsConfig.perSource[metricKey];
          const displayName = config?.displayName || metricKey;
          return (
            <div key={metricKey} style={{marginBottom: index < metricKeys.length - 1 ? 4 : 0}}>
              {displayName}
            </div>
          );
        })}
      </div>
    );
  }, [metricKeys, metricsConfig]);

  const buttonContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
      }}
    >
      {direction === 'right' && showCount && (
        <Typography
          variant="caption"
          style={{
            fontSize: 12,
            lineHeight: 1,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {remainingCount}
          <br />
          <span style={{fontSize: 10, fontWeight: 500}}>more</span>
        </Typography>
      )}
      <IconButton
        size="big"
        disabled={disabled}
        color="primary"
        style={{
          opacity: disabled ? 0.3 : 1,
        }}
      >
        <Icon />
      </IconButton>
      {direction === 'left' && showCount && (
        <Typography
          variant="caption"
          style={{
            fontSize: 12,
            lineHeight: 1,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {remainingCount}
          <br />
          <span style={{fontSize: 10, fontWeight: 500}}>more</span>
        </Typography>
      )}
    </div>
  );

  return (
    <StyledTableCell
      align="center"
      onClick={disabled ? undefined : onClick}
      style={{
        background: theme.palette.background.paper,
        width: NAV_CELL_WIDTH,
        padding: 8,
        cursor: disabled ? 'default' : 'pointer',
        ...(style || {}),
      }}
    >
      {tooltipContent ? (
        <Tooltip title={tooltipContent} arrow placement={direction === 'left' ? 'right' : 'left'}>
          {buttonContent}
        </Tooltip>
      ) : (
        buttonContent
      )}
    </StyledTableCell>
  );
};

export default CarouselNavigationCell;
